import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import { NetworkAPI } from "apis";
import LoadingUI from "components/common/LoadingUI";
import GamUprTable from "./GamUprTable";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import SelectedStickyFooter from "components/common/SelectedStickyFooter";
import ItemsFilter from "components/ops-mgmt/common/ItemsFilter";
import ChangeLogModal from "../ChangeLogModal";
import ChangeLogsStatus from "../ChangeLogsStatus";

import { searchIncludes } from "helpers/Utils";
import { PRICE_TYPE_BY_NAME } from "constants/PriceType";
import { setQueryString } from "helpers/QueryString";

import { PPFILTER, RULE_FILTER, PRICE, PRICING_RULE } from "../Constants";
import {
  createRuleFilters,
  createPPFilters,
  createPriceTypeFilters,
  getPriceFilters,
  getI2wResourceMappingFilters,
  isSectionEmpty,
} from "./GamUprHelper";
import { getFilteredLogsByRule } from "../DataHelpers";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 whitespace-pre shadow hover:bg-gray-300 text-gray-900 font-semibold";

const buttonDisabledClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-200 whitespace-pre text-gray-400 font-semibold cursor-not-allowed";

const buttonClass =
  "px-2 py-1 bg-gray-100 text-xs rounded border border-gray-400 hover:bg-gray-200 text-gray-900 font-semibold";

class NetworkGamUprViewer extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);

    this.state = {
      isSaving: false,
      isLoading: false,
      isLogLoading: false,

      networkId: null,
      networkInfo: null,
      segmentData: null,
      customData: null,
      placement: null,
      demandSource: null,
      errMsg: "",

      updatedAt: null,
      originalUprs: null,
      newUprs: null,

      ruleFilters: null,
      priceTypeFilters: null,
      resourceFilters: null,
      ppFilters: null,
      priceFilters: null,
      customPrice: 10,
      currentFilter: {
        ruleType: RULE_FILTER.ALL,
        priceType: "ALL", // when Publisher Rules
        ppType: PPFILTER.ALL, // when Publisher Rules
        price: PRICE.ALL, // when Publisher Rules
        resourceType: "ALL", // when Intowow Rules
      },

      isJsonView: false,
      inputValue: "",

      selectedIds: [],
      selectedKey: "",

      isChangeModalOpen: false,
      logIds: [],
      activeChangeLogs: [],
      inactiveChangeLogs: [],
      logFrom: moment().subtract(29, "days").valueOf(),
    };

    this.searchRef = React.createRef();

    this.getChangeLogs = this.getChangeLogs.bind(this);
    this.toggleJsonView = this.toggleJsonView.bind(this);
    this.handlePrepareFilters = this.handlePrepareFilters.bind(this);
    this.handleRuleTypeFilter = this.handleRuleTypeFilter.bind(this);
    this.handlePPTypeFilter = this.handlePPTypeFilter.bind(this);
    this.handlePriceTypeFilter = this.handlePriceTypeFilter.bind(this);
    this.handlePriceFilter = this.handlePriceFilter.bind(this);
    this.handlePriceChange = this.handlePriceChange.bind(this);
    this.handleForceRefresh = this.handleForceRefresh.bind(this);
    this.handleResourceTypeFilter = this.handleResourceTypeFilter.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleSelectedByKey = this.handleSelectedByKey.bind(this);
    this.handleImportSelected = this.handleImportSelected.bind(this);

    this.handleCheckLog = this.handleCheckLog.bind(this);
    this.handleCheckSelected = this.handleCheckSelected.bind(this);
  }

  async componentDidMount() {
    this._isMounted = true;
    this.setState({ isLoading: true });
    const { networkId, networkInfo } = this.props;
    if (!networkId) {
      this.setState({ errMsg: "Missing Network ID" });
      return;
    } else {
      this.setState({ networkId, networkInfo });
    }

    try {
      let { originalUprs, allUprs, updatedAt, placement } =
        await this._getGamUprs({
          isForceRefresh: false,
        });

      this.getChangeLogs(originalUprs);

      const { config } = await NetworkAPI.getNetworkUprSegmentation({
        networkId,
      });

      const demandSource = await NetworkAPI.getNetworkDemandSources({
        networkId,
      });

      const { customSettings } = await NetworkAPI.getNetworkCustomUpr({
        networkId,
      });
      _.forEach(customSettings, (c) => {
        if (c.targetingSection && c.targetingSection.customs) {
          if (!_.isArray(c.targetingSection.customs[0])) {
            c.targetingSection.customs = [c.targetingSection.customs];
          }
        }
      });

      const {
        ppFilters,
        resourceFilters,
        ruleFilters,
        priceTypeFilters,
        priceFilters,
      } = this.handlePrepareFilters(allUprs);

      this.setState({
        originalUprs,
        newUprs: allUprs,
        ppFilters,
        resourceFilters,
        ruleFilters,
        priceTypeFilters,
        priceFilters,
        updatedAt,
        segmentData: config,
        customData: customSettings,
        placement,
        demandSource,

        isLoading: false,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async getChangeLogs(uprList) {
    const { networkId } = this.props;
    try {
      this.setState({
        isLogLoading: true,
      });
      const changeLogs = await NetworkAPI.getGamUprChangeLogs({
        networkId: _.toNumber(this.props.networkId),
        fromTimestamp: this.state.logFrom,
      });

      const allChangeLogsId = _.map(changeLogs || [], (log) => log.entityId);
      const uprExternalIds = uprList.map((upr) => upr.externalId);
      const missingIds = _.difference(allChangeLogsId, uprExternalIds);

      const inactiveRules = [];
      if (missingIds.length > 0) {
        const reuslt = await NetworkAPI.getGamUprByIds({
          networkId,
          externalIds: missingIds,
        });
        inactiveRules.push(...reuslt);
      }

      if (!this._isMounted) return;

      this.setState({
        activeChangeLogs: getFilteredLogsByRule(changeLogs, uprList),
        inactiveChangeLogs: getFilteredLogsByRule(changeLogs, inactiveRules),
        isLogLoading: false,
      });
    } catch (err) {
      console.log(err);
    }
  }

  async _getGamUprs({ isForceRefresh = false }) {
    const { networkId } = this.props;
    const { uprs, updatedAt } = await NetworkAPI.getGamUprs({
      networkId,
      isForceRefresh,
      status: "ACTIVE",
    });

    const [resourceMapping, networkFeatures] = await Promise.all([
      NetworkAPI.getNetworkResourceMapping({ networkId }),
      NetworkAPI.getRawNetworkFeatures({ networkId }),
    ]);

    const { placement, upr = {}, pub_upr } = resourceMapping;
    const { config } = networkFeatures;

    const protectedUprIds =
      _.get(pub_upr, ["mapping", "protectedIds"]) ||
      _.get(config, "publisherUPRProtectionList", []);
    const preservedUprIds =
      _.get(pub_upr, ["mapping", "preservedIds"]) ||
      _.get(config, "preservedUPRIds", []);

    const resourceById = _.keyBy(upr.mapping, (item) => {
      return item.id;
    });

    const allUprs = this._transformRules({
      uprs: _.cloneDeep(uprs),
      protectedUprIds,
      preservedUprIds,
      resourceById,
    });

    if (!this._isMounted) return;

    return {
      originalUprs: uprs,
      allUprs,
      updatedAt,
      placement: _.get(placement, "mapping") || [],
    };
  }

  _transformRules({ uprs, protectedUprIds, preservedUprIds, resourceById }) {
    let allUprs = _.map(uprs, (upr) => {
      let priceSection = {};
      const setForEverythingSetting = _.find(upr.priceSection.priceSettings, {
        isSetForEverything: true,
      });
      if (setForEverythingSetting) {
        priceSection.priceType = setForEverythingSetting.priceType;
        priceSection.price = setForEverythingSetting.price;
        priceSection.currency = setForEverythingSetting.currency;
      } else {
        priceSection.priceType = PRICE_TYPE_BY_NAME.BRAND_SETTING;
        priceSection.currency = _.get(upr.priceSection.priceSettings, [
          0,
          "currency",
        ]);
      }
      priceSection.brandedPriceSettings = upr.priceSection.priceSettings.filter(
        (setting) => !setting.isSetForEverything
      );

      let targetingSection = {};
      if (!isSectionEmpty(upr.targetingSection.inventoryUrl)) {
        targetingSection.inventoryUrl = upr.targetingSection.inventoryUrl;
      }
      if (!isSectionEmpty(upr.targetingSection.inventoryTypes)) {
        targetingSection.inventoryTypes = upr.targetingSection.inventoryTypes;
      }
      if (!isSectionEmpty(upr.targetingSection.inventoryFormat)) {
        targetingSection.inventoryFormat = upr.targetingSection.inventoryFormat;
      }
      if (upr.targetingSection.inventory) {
        if (!isSectionEmpty(upr.targetingSection.inventory.unitIds)) {
          _.set(
            targetingSection,
            ["inventory", "units"], // units not unitIds!
            upr.targetingSection.inventory.unitIds
          );
        }
        if (!isSectionEmpty(upr.targetingSection.inventory.placementIds)) {
          _.set(
            targetingSection,
            ["inventory", "placementIds"],
            upr.targetingSection.inventory.placementIds
          );
        }
        if (!isSectionEmpty(upr.targetingSection.inventory.mcm)) {
          _.set(
            targetingSection,
            ["inventory", "mcm"],
            upr.targetingSection.inventory.mcm
          );
        }
      }
      if (!isSectionEmpty(upr.targetingSection.browser)) {
        targetingSection.browser = upr.targetingSection.browser;
      }
      if (!isSectionEmpty(upr.targetingSection.country)) {
        targetingSection.country = upr.targetingSection.country;
      }
      if (!isSectionEmpty(upr.targetingSection.customs)) {
        targetingSection.customs = upr.targetingSection.customs;
      }
      if (!isSectionEmpty(upr.targetingSection.deviceCategory)) {
        targetingSection.deviceCategory = upr.targetingSection.deviceCategory;
      }
      if (!isSectionEmpty(upr.targetingSection.mobileApplication)) {
        targetingSection.mobileApplication =
          upr.targetingSection.mobileApplication;
      }
      if (!isSectionEmpty(upr.targetingSection.os)) {
        targetingSection.os = upr.targetingSection.os;
      }
      if (!isSectionEmpty(upr.targetingSection.videoPosition)) {
        targetingSection.videoPosition = upr.targetingSection.videoPosition;
      }

      upr.__isI2wRule = _.startsWith(upr.uprName, "Intowow_");
      upr.__isPubRule = !_.startsWith(upr.uprName, "Intowow_");
      upr.__isProtected =
        _.indexOf(protectedUprIds, _.parseInt(upr.externalId)) !== -1;
      upr.__isPreserved =
        _.indexOf(preservedUprIds, _.parseInt(upr.externalId)) !== -1;

      return {
        ...upr,
        priceSection,
        targetingSection,
        props: _.get(resourceById, [upr.externalId, "props"], null),
      };
    });
    allUprs = _.sortBy(allUprs, "uprName");
    return allUprs;
  }

  async handleForceRefresh() {
    const {
      currentFilter: { resourceType, ruleType, price },
    } = this.state;

    this.setState({
      isLoading: true,
      updatedAt: null,
      originalUprs: null,
      newUprs: null,
      statusFilters: null,
      ruleFilters: null,
      priceTypeFilters: null,
      resourceFilters: null,
      ppFilters: null,
      priceFilters: null,
      selectedIds: [],
      selectedKey: "",
      activeChangeLogs: [],
      inactiveChangeLogs: [],
    });

    try {
      const { originalUprs, allUprs, updatedAt } = await this._getGamUprs({
        isForceRefresh: true,
      });

      this.getChangeLogs(originalUprs);

      const {
        ppFilters,
        resourceFilters,
        ruleFilters,
        priceTypeFilters,
        priceFilters,
      } = this.handlePrepareFilters(allUprs);

      const ruleSelected = _.find(ruleFilters, { key: ruleType });
      let newUprs = ruleSelected.uprs;
      let tempResourceType = resourceType;
      if (ruleType === RULE_FILTER.INTOWOW_ONLY) {
        const tempResource = _.find(resourceFilters, { key: resourceType });
        if (!tempResource) tempResourceType = "ALL";
        newUprs = _.find(resourceFilters, { key: tempResourceType }).uprs;
      } else if (ruleType === RULE_FILTER.PUBLISHER_ONLY) {
        newUprs = _.find(priceFilters, { key: price }).uprs;
      }

      this.setState({
        originalUprs,
        newUprs,
        ruleFilters,
        priceTypeFilters,
        resourceFilters,
        priceFilters,
        ppFilters,
        updatedAt,
        currentFilter: {
          ...this.state.currentFilter,
          resourceType: tempResourceType,
        },
        isLoading: false,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  toggleJsonView() {
    this.setState({ isJsonView: !this.state.isJsonView });
  }

  handlePrepareFilters(newUprs) {
    const {
      currentFilter: { ppType, priceType },
      customPrice,
      networkInfo,
    } = this.state;

    const ruleFilters = createRuleFilters(newUprs);

    // RULEFILTER.PUBLISHER_ONLY
    const ppFilters = createPPFilters(ruleFilters[2]);
    const ppSelected = _.find(ppFilters, { key: ppType });
    const priceTypeFilters = createPriceTypeFilters(ppSelected);
    const priceTypeSelected = _.find(priceTypeFilters, {
      key: priceType,
    });
    const priceFilters = getPriceFilters(
      priceTypeSelected.uprs,
      customPrice,
      networkInfo.currency
    );

    // RULEFILTER.INTOWOW_ONLY
    const resourceFilters = getI2wResourceMappingFilters(ruleFilters[1]);

    return {
      ppFilters,
      resourceFilters,
      ruleFilters,
      priceTypeFilters,
      priceFilters,
    };
  }

  handleRuleTypeFilter(selectedFilter) {
    // currentFilter: {
    //   ruleType: "PUBLISHER_ONLY",
    //   priceType: "ALL",
    // },

    // selectedFilter: {key, name, uprs}

    const newUprs = selectedFilter.uprs;
    const currentFilter = {
      ...this.state.currentFilter,
      ruleType: selectedFilter.key,
      resourceType: "ALL",
      ppType: PPFILTER.ALL,
      priceType: "ALL",
      price: PRICE.ALL,
    };

    this.setState({ newUprs, currentFilter });
  }

  handlePPTypeFilter(selectedFilter) {
    // currentFilter: {
    //   ruleType: "PUBLISHER_ONLY",
    //   priceType: "ALL",
    // },

    // selectedFilter: {key, name, uprs}

    const newUprs = selectedFilter.uprs;
    let currentFilter = {
      ...this.state.currentFilter,
      ppType: selectedFilter.key,
      priceType: "ALL",
      price: PRICE.ALL,
    };
    const priceTypeFilters = createPriceTypeFilters(selectedFilter);
    const priceFilters = getPriceFilters(
      selectedFilter.uprs,
      this.state.customPrice,
      this.state.networkInfo.currency
    );
    this.setState({
      newUprs,
      currentFilter,
      priceTypeFilters,
      priceFilters,
    });
  }

  handlePriceTypeFilter(selectedFilter) {
    const newUprs = selectedFilter.uprs;
    let currentFilter = {
      ...this.state.currentFilter,
      priceType: selectedFilter.key,
      price: PRICE.ALL,
    };

    const priceFilters = getPriceFilters(
      newUprs,
      this.state.customPrice,
      this.state.networkInfo.currency
    );
    this.setState({ newUprs, currentFilter, priceFilters });
  }

  handlePriceFilter(selectedFilter) {
    const newUprs = selectedFilter.uprs;
    let currentFilter = {
      ...this.state.currentFilter,
      price: selectedFilter.key,
    };

    this.setState({ newUprs, currentFilter });
  }

  handlePriceChange(e) {
    this.setState({ customPrice: e.target.value }, () => {
      // Update price filter
      const newUprs = this.state.priceFilters.find(
        (pf) => pf.key === PRICE.ALL
      ).uprs;
      const priceFilters = getPriceFilters(
        newUprs,
        this.state.customPrice,
        this.state.networkInfo.currency
      );
      const customUpr = priceFilters.find((pf) => pf.key === PRICE.CUSTOM);

      this.setState({
        newUprs: customUpr ? customUpr.uprs : [],
        priceFilters,
      });
    });
  }

  handleResourceTypeFilter(selectedFilter) {
    const newUprs = selectedFilter.uprs;
    let currentFilter = {
      ...this.state.currentFilter,
      resourceType: selectedFilter.key,
    };
    this.setState({ newUprs, currentFilter });
  }

  handleSearch(inputValue) {
    this.setState({ inputValue });
  }

  handleSelect(selected) {
    this.setState({ selectedIds: selected });
  }

  handleSelectedByKey(key) {
    this.setState({ selectedKey: key });
  }

  handleImportSelected() {
    const { selectedIds, networkId } = this.state;
    const queryString = setQueryString(
      this.props,
      { importId: selectedIds },
      true
    );
    window.open(
      `${window.location.origin}/#/ops-mgmt/account/network/${networkId}/upr-setting?${queryString}`
    );
  }

  handleCheckLog(id) {
    this.setState({
      isChangeModalOpen: true,
      logIds: [id],
    });
  }

  handleCheckSelected() {
    const { selectedIds } = this.state;
    this.setState({
      isChangeModalOpen: true,
      logIds: selectedIds,
    });
  }

  render() {
    const { networkId, networkInfo, units, virtualPlacements } = this.props;
    const {
      isLogLoading,
      isLoading,
      errMsg,

      updatedAt,
      originalUprs,
      newUprs,
      segmentData,
      customData,
      placement,
      demandSource,

      ruleFilters,
      priceTypeFilters,
      currentFilter,
      resourceFilters,
      ppFilters,
      priceFilters,
      customPrice,

      isJsonView,
      selectedIds,
      selectedKey,

      isChangeModalOpen,
      activeChangeLogs,
      inactiveChangeLogs,
      logIds,
      logFrom,
    } = this.state;

    const end = moment();
    const start = moment(updatedAt);
    const duration = moment.duration(end.diff(start));
    const updatedAgo = duration.asHours();

    const filteredUprs = !_.isNil(newUprs)
      ? newUprs.filter((upr) => {
          return searchIncludes(upr.uprName, this.state.inputValue);
        })
      : [];

    const selectedByKey =
      selectedKey === ""
        ? []
        : selectedIds.map((extId) => {
            return _.get(
              _.find(originalUprs, { externalId: extId }),
              selectedKey
            );
          });

    const disableCheckLogs = !_.some(selectedIds, (id) =>
      _.find(activeChangeLogs, { entityId: id })
    );

    return (
      <>
        <div>
          <PageTitle title="GAM UPRs"></PageTitle>

          {isLoading && <LoadingUI></LoadingUI>}
          {errMsg && <div className="text-red-800">{errMsg}</div>}

          {newUprs && (
            <div>
              <div className="mb-4 flex items-center justify-end gap-2">
                <div className="mr-auto">
                  <ChangeLogsStatus
                    changeLogs={[...activeChangeLogs, ...inactiveChangeLogs]}
                    isLogLoading={isLogLoading}
                    logFrom={logFrom}
                    handleView={() =>
                      this.setState({ isChangeModalOpen: true, logIds: [] })
                    }
                  />
                </div>

                <div className="mr-2 text-sm">
                  Updated at:{" "}
                  <DateTimeFormatter
                    datetime={updatedAt}
                    timezone={networkInfo.timezone}
                  ></DateTimeFormatter>
                  {updatedAgo > 1 && (
                    <span className="pl-4 font-semibold text-red-600">
                      (Rules could be outdated)
                    </span>
                  )}
                </div>

                <button className={buttonClass} onClick={this.toggleJsonView}>
                  {!isJsonView ? "View original JSON" : "View in table"}
                </button>

                <button
                  className={buttonClass}
                  onClick={this.handleForceRefresh}
                >
                  Force Refresh
                </button>
              </div>

              {!isJsonView && updatedAt && (
                <div>
                  {ruleFilters && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        Rule Filter:{" "}
                      </div>
                      {ruleFilters.map((item) => {
                        const isSelected = item.key === currentFilter.ruleType;
                        return (
                          <button
                            key={item.name}
                            type="button"
                            className={`border rounded mb-1 mr-1 border-gray-200 font-medium ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handleRuleTypeFilter(item);
                            }}
                          >
                            {item.name} ({item.uprs.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {currentFilter.ruleType === "INTOWOW_ONLY" && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        UPR Resource Filter:{" "}
                      </div>
                      {resourceFilters.map((item) => {
                        const isSelected =
                          currentFilter.resourceType === item.key;
                        return (
                          <button
                            key={item.name}
                            type="button"
                            className={`border rounded mb-1 mr-1 border-gray-200 ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handleResourceTypeFilter(item);
                            }}
                          >
                            {item.name} ({item.uprs.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {currentFilter.ruleType === "PUBLISHER_ONLY" && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        Protected or Preserved Filter:{" "}
                      </div>
                      {ppFilters.map((item) => {
                        const isSelected = currentFilter.ppType === item.key;
                        return (
                          <button
                            key={item.name}
                            type="button"
                            className={`border rounded mb-1 mr-1 border-gray-200 ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handlePPTypeFilter(item);
                            }}
                          >
                            {item.name} ({item.uprs.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {currentFilter.ruleType === "PUBLISHER_ONLY" && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        Price Type Filter:{" "}
                      </div>
                      {priceTypeFilters.map((item) => {
                        const isSelected = currentFilter.priceType === item.key;
                        return (
                          <button
                            key={item.name}
                            type="button"
                            className={`border rounded mb-1 mr-1 border-gray-200 ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handlePriceTypeFilter(item);
                            }}
                          >
                            {item.name} ({item.uprs.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {currentFilter.ruleType === "PUBLISHER_ONLY" && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        Price Filter:{" "}
                      </div>
                      {priceFilters.map((item) => {
                        const isSelected = currentFilter.price === item.key;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={`border rounded mb-1 mr-1 flex gap-1 border-gray-200 ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handlePriceFilter(item);
                            }}
                          >
                            {item.name}{" "}
                            {item.key === PRICE.CUSTOM && (
                              <input
                                type="number"
                                value={customPrice}
                                className="border rounded focus:outline-none flex appearance-none border-gray-400 bg-white px-1 leading-normal hover:border-gray-500 focus:border-blue-400 focus:shadow-inner"
                                style={{ width: "90px" }}
                                onChange={this.handlePriceChange}
                              ></input>
                            )}{" "}
                            ({item.uprs.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-2">
                    <ItemsFilter
                      ref={this.searchRef}
                      inputValue={this.state.inputValue}
                      handleSearch={this.handleSearch}
                      placeholder={"Search by UPR name"}
                    ></ItemsFilter>
                  </div>
                </div>
              )}

              <div className="flex w-full">
                <div className="w-full">
                  <div className="mb-1 mt-2 flex items-center justify-between">
                    <div className="text-lg font-semibold">
                      GAM UPRs (
                      {isJsonView
                        ? (originalUprs || []).length
                        : (filteredUprs || []).length}
                      ):{" "}
                    </div>
                  </div>

                  {updatedAt && (
                    <>
                      {isJsonView ? (
                        <textarea
                          style={{ width: "100%", height: "600px" }}
                          defaultValue={JSON.stringify(originalUprs, null, 4)}
                          readOnly
                        ></textarea>
                      ) : (
                        <div style={{ marginBottom: "600px" }}>
                          <GamUprTable
                            items={filteredUprs}
                            networkInfo={networkInfo}
                            networkId={networkId}
                            isI2wRuleSelected={
                              currentFilter.ruleType === "INTOWOW_ONLY"
                            }
                            relevantLogs={activeChangeLogs}
                            segmentData={segmentData}
                            customData={customData}
                            placement={placement}
                            demandSource={demandSource}
                            units={units}
                            virtualPlacements={virtualPlacements}
                            handleSelect={this.handleSelect}
                            handleCheckLog={this.handleCheckLog}
                            selectedItems={selectedIds}
                          ></GamUprTable>
                          <SelectedStickyFooter
                            idList={["externalId"]}
                            selectedItems={selectedByKey}
                            handleSelectedByKey={this.handleSelectedByKey}
                            handleClear={() => this.handleSelect([])}
                            actionButtons={
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={selectedIds.length === 0}
                                  className={
                                    selectedIds.length > 0
                                      ? buttonActionClass
                                      : buttonDisabledClass
                                  }
                                  onClick={this.handleImportSelected}
                                >
                                  Import Selected
                                </button>
                                <button
                                  type="button"
                                  disabled={disableCheckLogs}
                                  className={
                                    !disableCheckLogs && selectedIds.length > 0
                                      ? buttonActionClass
                                      : buttonDisabledClass
                                  }
                                  onClick={this.handleCheckSelected}
                                >
                                  Check Logs
                                </button>
                              </div>
                            }
                          ></SelectedStickyFooter>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {isChangeModalOpen && (
                <ChangeLogModal
                  title="UPR"
                  pricingRule={PRICING_RULE.UPR}
                  networkId={networkId}
                  rules={originalUprs}
                  changeLogs={[...activeChangeLogs, ...inactiveChangeLogs]}
                  timezone={networkInfo.timezone}
                  logIds={logIds}
                  logFrom={logFrom}
                  onClose={() => this.setState({ isChangeModalOpen: false })}
                ></ChangeLogModal>
              )}
            </div>
          )}
        </div>
      </>
    );
  }
}

function PageTitle({ title }) {
  return <div className="text-3xl font-extrabold">{title}</div>;
}

export default NetworkGamUprViewer;
